import { DateTime } from 'luxon';
import { transformToCurrency } from './const';

export function formatPartialOrderHash(hash: string) {
  return hash.slice(0, 2) + hash.slice(-2);
}

export function determinateDaysAgo(date: number | string) {
  const now = new Date();
  const orderDate = new Date(date);
  const diffTime = Math.abs(now.getTime() - orderDate.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
}

export function formatDateToShort(input: string): string {
  const date = new Date(input);

  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const year = String(date.getUTCFullYear()).slice(-2);

  return `${day}/${month}/${year}`;
}

export function convertToZone(fechaStr: string, format: string = 'dd/MM/yyyy HH:mm', zone: string = 'America/Bogota',): string {
  const fechaUTC = DateTime.fromFormat(fechaStr, format, { zone: 'UTC' });
  const fechaColombia = fechaUTC.setZone(zone);
  return fechaColombia.toFormat(format);
}

function parseDateAsUtc(isoOrDate: string | Date): DateTime {
  if (typeof isoOrDate !== 'string') {
    return DateTime.fromJSDate(isoOrDate, { zone: 'utc' });
  }
  const s = isoOrDate.trim();
  let dt = DateTime.fromISO(s, { zone: 'utc' });
  if (dt.isValid) return dt;
  dt = DateTime.fromFormat(s, 'yyyy-MM-dd HH:mm:ss.SSSSSS', { zone: 'utc' });
  if (dt.isValid) return dt;
  dt = DateTime.fromFormat(s, 'yyyy-MM-dd HH:mm:ss', { zone: 'utc' });
  if (dt.isValid) return dt;
  return DateTime.fromISO(s, { zone: 'utc' });
}

export function formatDateInZone(
  isoOrDate: string | Date | null | undefined,
  zone: string,
  formatStr: string = 'dd/MM/yyyy HH:mm'
): string {
  if (isoOrDate == null || isoOrDate === '') return '—';
  const dt = parseDateAsUtc(isoOrDate);
  if (!dt.isValid) return typeof isoOrDate === 'string' ? isoOrDate : '—';
  return dt.setZone(zone).toFormat(formatStr);
}


const DEFAULT_TIMEZONE_BY_COUNTRY: Record<string, string> = {
  AR: 'America/Buenos_Aires',
  BO: 'America/La_Paz',
  BR: 'America/Sao_Paulo',
  CA: 'America/Toronto',
  CL: 'America/Santiago',
  CO: 'America/Bogota',
  EC: 'America/Guayaquil',
  GT: 'America/Guatemala',
  MX: 'America/Mexico_City',
  PY: 'America/Asuncion',
  PE: 'America/Lima',
  US: 'America/New_York',
  UY: 'America/Montevideo',
  VE: 'America/Caracas',
};


export function getDefaultTimezoneForCountry(countryCode: string, fallback: string): string {
  const zone = DEFAULT_TIMEZONE_BY_COUNTRY[countryCode];
  if (!zone) return fallback;
  try {
    const supported = (Intl as any).supportedValuesOf?.('timeZone') as string[] | undefined;
    if (supported && supported.includes(zone)) return zone;
  } catch {
    // ignore
  }
  return fallback;
}

/** Offset en minutos respecto a UTC (ej: -300 para UTC-05:00). Para ordenar zonas. */
export function getTimezoneOffsetMinutes(zone: string): number {
  const dt = DateTime.now().setZone(zone);
  return dt.isValid ? dt.offset : 0;
}

export function getTimezoneUtcOffset(zone: string): string {
  const dt = DateTime.now().setZone(zone);
  if (!dt.isValid) return '';
  const offsetMinutes = dt.offset;
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const hours = Math.floor(Math.abs(offsetMinutes) / 60);
  const mins = Math.abs(offsetMinutes) % 60;
  return `UTC${sign}${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
}

export function getTimezoneSearchText(zone: string): string {
  const offset = getTimezoneUtcOffset(zone);
  if (!offset) return zone.toLowerCase();
  const dt = DateTime.now().setZone(zone);
  const offsetMinutes = dt.offset;
  const hours = Math.floor(Math.abs(offsetMinutes) / 60);
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const shortOffset = `utc${sign}${hours}`;
  const signAndHour = `${sign}${hours}`;
  const onlyTime = offset.replace(/^UTC[+-]/, '').toLowerCase();
  return [zone, offset, shortOffset, signAndHour, onlyTime].join(' ').toLowerCase();
}

export const formatCOP = (value: number, isDollar: boolean = false): string => {
  const formatted = value.toFixed(2);
  const [whole, decimal] = formatted.split('.');
  const formattedWhole = whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.');
  const result = `${formattedWhole}${decimal === '00' ? '' : ',' + decimal}`;

  if (isDollar) {
    return transformToCurrency(Number(value));
  }

  return `$${result}`;
}

// formate date to string to yyyy-mm-dd hh:mm:ss
export const formatDateToISOString = (date: Date): string => {
  const pad = (n: number) => n.toString().padStart(2, "0");
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};